import { httpsCallable } from 'firebase/functions';
import { collection, collectionGroup, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db, getFns } from '../firebase';
import { listLocalSecrets } from './local-secrets-client';
const isLocalDev = import.meta.env.DEV && location.hostname === 'localhost';

const canUseStorage = typeof window !== 'undefined' && window.localStorage;
const readJson = (key, fallback) => {
  if (!canUseStorage) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const writeJson = (key, value) => {
  if (!canUseStorage) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

function call(name, data) {
  const fn = httpsCallable(getFns(), name);
  const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  return fn(data)
    .then(r => r.data)
    .catch(err => {
      const msg = (err?.message || err?.code || 'callable failed') + '';
      const is403 = /permission|403|invoker|unauthorized/i.test(msg);
      const elapsed = (typeof performance !== 'undefined' && performance.now) ? Math.round(performance.now() - t0) : (Date.now() - t0);
      return {
        ok: false,
        status: is403 ? 403 : 0,
        latencyMs: elapsed,
        bodySnippet: msg.slice(0, 800),
        hint: is403
          ? 'HTTP 403: Hosting cannot invoke a private Cloud Run service. Grant Cloud Run Invoker to Hosting agent OR route via callable (recommended).'
          : 'Callable failed. Check Functions logs.'
      };
    });
}

export const setIntegrationConfig = (name, data)     => call('setIntegrationConfig', { name, data });
export const getIntegrationLogs   = (name, opts={})  => call('getIntegrationLogs', { name, ...opts });

export async function getIntegrationMeta(name) {
  if (isLocalDev) {
    const ls = await listLocalSecrets();
    const hasLocal = ({
      shopify: !!ls.secrets?.SHOPIFY_TOKEN,
      claude: !!ls.secrets?.CLAUDE_API_KEY,
      podium:  !!ls.secrets?.PODIUM_API_KEY,
      google_drive: !!ls.secrets?.GOOGLE_SA_JSON_B64,
      google_calendar: !!ls.secrets?.GOOGLE_SA_JSON_B64,
      google_sheets: !!ls.secrets?.GOOGLE_SA_JSON_B64,
    })[name];
    return { config:{}, status:'Unknown', lastChecked:null, latencyMs:null, hasSecret: !!hasLocal };
  } else {
    const fn = httpsCallable(getFns(), 'getIntegrationMeta');
    const r = await fn({ name });
    return r.data;
  }
}

export async function downloadIntegrationLogs(name) {
  // Pull a page and trigger a client-side download as JSON
  const res = await getIntegrationLogs(name, { limit: 200 });
  const blob = new Blob([JSON.stringify(res?.items || [], null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}-integration-logs.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Optional helper for UIs to present friendly messages per status
export function friendlyStatusMessage(status, ok) {
  if (status === 401 || status === 403) return 'Unauthorized: check token/secret and required scopes.';
  if (status === 404) return 'Not found: verify shop domain / spreadsheetId / calendarId.';
  if (status === 429) return 'Rate limited: wait and retry.';
  if (status === 0)   return 'Network/timeout. The server could not reach the API.';
  if (ok)             return 'Connected successfully.';
  return 'API returned an error.';
}

// Dev proxy for integration tests; falls back to callable in prod
export async function testIntegration(payload) {
  if (isLocalDev) {
    const r = await fetch('/__intg/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: payload.name, payload })
    });
    return r.json();
  } else {
    const fn = httpsCallable(getFns(), 'testIntegration');
    const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    try {
      const res = await fn(payload);
      return res.data;
    } catch (err) {
      const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      const msg = (err?.message || err?.code || 'callable failed') + '';
      const is403 = /permission|403|invoker/i.test(msg);
      return {
        ok: false,
        status: is403 ? 403 : 0,
        latencyMs: Math.round(now - t0),
        bodySnippet: msg.slice(0, 800),
        hint: is403 ? 'Cloud Run Invoker or auth issue' : 'Callable failed'
      };
    }
  }
}

// ---- Admin data helpers (Firestore/localStorage fallbacks) ----

export async function listErrors({ limit: limitCount = 100 } = {}) {
  try {
    const q = query(collectionGroup(db, 'events'), orderBy('at', 'desc'), limit(limitCount * 3));
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => {
      const data = d.data() || {};
      const parent = d.ref?.parent?.parent;
      const service = parent?.id || data.service || 'unknown';
      const status = data.status;
      const ok = data.ok;
      const severity = ok === false || (typeof status === 'number' && status >= 400)
        ? 'error'
        : (data.severity || 'info');
      const msg = data.message || data.bodySnippet || data.error || data.type || '';
      const at = data.at || data.createdAt || data.timestamp || null;
      return { id: d.id, at, service, severity, msg, ok, status };
    });
    const errorsOnly = rows.filter((r) => r.severity === 'error' || r.ok === false || (typeof r.status === 'number' && r.status >= 400));
    return errorsOnly.slice(0, limitCount);
  } catch {
    return [];
  }
}

export async function getFeatureFlags() {
  return readJson('feature_flags', {});
}

export async function setFeatureFlag(key, value) {
  const flags = readJson('feature_flags', {});
  const next = { ...flags, [key]: !!value };
  writeJson('feature_flags', next);
  return next;
}

export async function listSignals() {
  return [];
}

export async function listApiKeys() {
  return readJson('api_keys', []);
}

export async function createApiKey({ name, scopes = [] } = {}) {
  const token = `ag_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  const record = {
    id: `key_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    name: name || 'Key',
    scopes,
    createdAt: new Date().toISOString(),
    last4: token.slice(-4),
    revoked: false,
  };
  const keys = readJson('api_keys', []);
  writeJson('api_keys', [record, ...keys]);
  return { token, record };
}

export async function revokeApiKey(id) {
  const keys = readJson('api_keys', []);
  const next = keys.map((k) => (k.id === id ? { ...k, revoked: true } : k));
  writeJson('api_keys', next);
  return { ok: true };
}
